import apiClient from '../phpAPIclient'

export const namespaced = true

export const state = {
  orderDirection: 'desc',
  orderByColumn: 'DatetimeUTC',
  initialSort: {
    sort: 'desc',
    sortField: 'DatetimeLocal'
  },
  bookings: [],
  bookingsCount: 0,
  currentPage: 1,
  totalPages: 1,
  bookingsPerPage: 25,
  isFetching: false,
  bookingsWereFetched: false,
  error: null,
  filters: {
    search: '',
    reservationDate: '',
    startDate: '',
    status: '',
    attendance: '',
    unchecked: false,
  },
  bookingStatusOptions: [
    {
      id: 'Error',
      label: 'Error',
    },
    {
      id: 'Concept',
      label: 'Concept',
    },
    {
      id: 'Active',
      label: 'Active',
    },
    {
      id: 'Test',
      label: 'Test',
    },
    {
      id: 'Cancelled',
      label: 'Cancelled',
    },
  ],
  attendanceOptions: [
    {
      id: 'CheckedIn',
      label: 'Checked In',
    },
    {
      id: 'Open',
      label: 'Open',
    },
    {
      id: 'NoShow',
      label: 'No Show',
    },
  ],
}

export const getters = {
  initialSort: ({ initialSort }) => initialSort,
  bookings: ({ bookings }) => bookings,
  bookingsCount: ({ bookingsCount }) => bookingsCount,
  currentPage: ({ currentPage }) => currentPage,
  totalPages: ({ totalPages }) => totalPages,
  bookingsPerPage: ({ bookingsPerPage }) => bookingsPerPage,
  filters: ({ filters }) => filters,
  appliedFilters: ({filters}) => {
    let appliedFilters = {}
    for (const [key, value] of Object.entries(filters)) {
      if (value) appliedFilters[key] = value
    }
    return appliedFilters
  },
  search: ({ filters }) => filters.search,
  isFetching: ({ isFetching }) => isFetching,
  bookingsWereFetched: ({ bookingsWereFetched }) => bookingsWereFetched,
  error: ({ error }) => error,
  filterQueryString: ({ orderDirection, orderByColumn, currentPage, bookingsPerPage, filters }) => {
    const start = (currentPage - 1) * bookingsPerPage
    let queries = [
      `length=${bookingsPerPage}`,
      `start=${start}`,
      `filter[orderDirection]=${orderDirection}`,
      `filter[orderByColumn]=${orderByColumn}`,
    ]
    for (const [key, value] of Object.entries(filters)) {
      if (key === 'search') {
        queries.push([`search[value]=${value}`])
      } else if (key === 'unchecked') {
        if (value === true) queries.push(['filter[unchecked]=1'])
      } else {
        queries.push([`filter[${key}]=${value}`])
      }
    }
    return queries.join('&')
  },
  bookingStatusOptions: ({ bookingStatusOptions }) => bookingStatusOptions,
  attendanceOptions: ({ attendanceOptions }) => attendanceOptions,
}

export const mutations = {
  fetch: state => {
    state.isFetching = true
  },
  setBookings: (state, data) => {
    state.bookings = data
    state.isFetching = false
  },
  setBookingsCount: (state, count) => {
    if (count <= state.bookingsPerPage) {
      state.totalPages = 1
      state.bookingsCount = count
    } else {
      const totalPages = Math.ceil(count / state.bookingsPerPage)
      state.totalPages = totalPages
      state.bookingsCount = count
    }
  },
  setCurrentPage: (state, page) => {
    state.currentPage = page
  },
  setRowsPerPage: (state, rows) => {
    state.bookingsPerPage = rows
  },
  fetchError: (state, error) => {
    state.error = error
    state.isFetching = false
    state.bookings = []
  },
  setFilters: (state, filters) => {
    if (filters.length > 0) {
      filters.forEach(({ key, value }) => {
        state.filters[key] = value
      })
    }
  },
  setFilter: (state, { filter, value }) => {
    state.filters[filter] = value
  },
  setDateFilter: (state, { filter, dates }) => {
    if (filter === 'bookingDate') {
      state.filters.reservationDate = dates
    }

    if (filter === 'visitDate') {
      state.filters.startDate = dates
    }
  },
  clearFilters: (state) => {
    state.filters = {
      search: '',
      reservationDate: '',
      startDate: '',
      status: '',
      attendance: '',
      unchecked: false,
    }
  },
  showAllStatusOptions: (state) => {
    const adminOptions = [
      {
        id: 'Pending',
        label: 'Pending',
      },
      {
        id: 'Expired',
        label: 'Expired',
      },
      {
        id: 'Rejected',
        label: 'Rejected',
      },
      {
        id: 'ShoppingCart',
        label: 'Shopping Cart',
      },
    ]
    state.bookingStatusOptions = [...state.bookingStatusOptions, ...adminOptions]
  },
  setOrderDirection: (state, direction) => {
    state.orderDirection = direction
  },
  setOrderByColumn: (state, field) => {
    state.orderByColumn = field
  },

}

export const actions = {
  async fetchBookings({ dispatch, commit, getters }) {
    const queryString = getters.filterQueryString
    const appliedFilters = getters.appliedFilters
    commit('fetch')
    try {
      dispatch('addParamsToLocation', appliedFilters)
      const result = await apiClient.get(`/bookings?${queryString}`)
      if (result.data.data) {
        const bookings = await dispatch('transformDataForTableUI', result.data.data)
        commit('setBookings', bookings)
        commit('setBookingsCount', result.data.recordsFiltered[0])
      }
    } catch (e) {
      console.error('Error while fetching bookings!')
      console.log({ error: e })
      commit('fetchError', e)
    }
  },
  setFilters({ commit }, filters) {
    commit('setFilters', filters)
  },
  setFilter({ commit }, { filter, value }) {
    if (filter === 'reservationDate' || filter === 'visitDate') {
      commit('setDateFilter', { filter, dates: value })
    } else {
      commit('setFilter', { filter, value })
    }
  },
  setDateFilter({ commit }, { filter, dates }) {
    commit('setDateFilter', { filter, dates })
  },
  setCurrentPage({ commit }, page) {
    commit('setCurrentPage', page)
  },
  setRowsPerPage({ commit }, rows) {
    commit('setRowsPerPage', rows)
  },
  decodeHashFilter(_, hashFilter) {
    const encoded = hashFilter.substring(1)
    const decoded = decodeURI(encoded)
    if (decoded) {
      const parsed = JSON.parse(decoded)
      return parsed
    } else {
      return {}
    }
  },
  transformDataForTableUI(_, bookings) {
    return bookings.map(booking => {
      return {
        ...booking,
        FullName: booking.booking_customer?.FullName,
        CompanyName: booking.booking_customer?.CompanyName,
      }
    })
  },
  async checkInBooking(_, key) {
    return await apiClient.get(`/bookings/${key}/checkin`)
  },
  async cancelBooking(_, key) {
    return await apiClient.get(`/bookings/${key}/cancel`)
  },
  async markAsNoShow(_, key) {
    return await apiClient.post(`/bookings/${key}/no-show`)
  },
  async sortWithAPI({commit}, { field, direction }) {
    await commit('setOrderDirection', direction)
    await commit('setOrderByColumn', field)
  },
  addParamsToLocation(_, appliedFilters) {
    let filters = []
    for (const [key, value] of Object.entries(appliedFilters)) {
      filters.push(`${key}=${value}`)
    }
    const path = `${window.location.origin}/bookings`
    if (filters.length > 0) {
      history.pushState({}, null, path + '?' + filters.join('&'))
    } else {
      history.pushState({}, null, path)
    }
  }
}
